import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import type { CoverageAreaInput, CoveragePartyInput } from "@/lib/coverage/coverageLookup";
import { kmToMiles } from "@/lib/coverage/coverageClient";

export type PartyDraft = {
  id: string | null;
  name: string;
  program: string;
  color: string;
  pricingUrl: string;
  address: string;
  preferredMiles: string;
  fullMiles: string;
  polygonText: string;
};

type Props = {
  parties: CoveragePartyInput[];
  areas: CoverageAreaInput[];
  draft: PartyDraft;
  busy: boolean;
  error: string | null;
  drawing: boolean;
  onChange: (next: PartyDraft) => void;
  onSelect: (id: string | null) => void;
  onSave: () => void;
  onDraw: () => void;
};

const field = "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

export function CoveragePartyEditor(props: Props) {
  const d = props.draft;
  function set(patch: Partial<PartyDraft>) {
    props.onChange({ ...d, ...patch });
  }
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Plants</h3>
        <button type="button" className="text-sm text-blue-700 dark:text-blue-300" onClick={() => props.onSelect(null)}>
          New
        </button>
      </div>
      <ul className="mt-2 max-h-28 space-y-1 overflow-auto text-sm">
        {props.parties.map((party) => (
          <li key={party.id}>
            <button
              type="button"
              className={party.id === d.id ? "font-semibold" : ""}
              onClick={() => props.onSelect(party.id)}
            >
              <span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: party.color }} />
              {party.name}
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-3 space-y-2">
        <label className="block text-xs text-gray-500">
          Name
          <input className={field} value={d.name} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="block text-xs text-gray-500">
          Program
          <input className={field} value={d.program} onChange={(e) => set({ program: e.target.value })} />
        </label>
        <label className="block text-xs text-gray-500">
          Color
          <input className={field} type="color" value={d.color} onChange={(e) => set({ color: e.target.value })} />
        </label>
        <label className="block text-xs text-gray-500">
          Pricing link
          <input className={field} value={d.pricingUrl} onChange={(e) => set({ pricingUrl: e.target.value })} placeholder="https://" />
        </label>
        <label className="block text-xs text-gray-500">
          Plant address
          <div className="mt-1">
            <AddressAutocompleteInput value={d.address} onChange={(address) => set({ address })} placeholder="Where this plant ships from" />
          </div>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs text-gray-500">
            Preferred miles
            <input className={field} value={d.preferredMiles} onChange={(e) => set({ preferredMiles: e.target.value })} />
          </label>
          <label className="block text-xs text-gray-500">
            Full miles
            <input className={field} value={d.fullMiles} onChange={(e) => set({ fullMiles: e.target.value })} />
          </label>
        </div>
        <label className="block text-xs text-gray-500">
          Polygon GeoJSON
          <textarea
            className={`${field} font-mono text-xs`}
            rows={4}
            value={d.polygonText}
            onChange={(e) => set({ polygonText: e.target.value })}
            placeholder='{"type":"Polygon","coordinates":[...]}'
          />
        </label>
        <div className="flex gap-2">
          <button type="button" className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600" onClick={props.onDraw}>
            {props.drawing ? "Drawing…" : "Draw territory"}
          </button>
          <button
            type="button"
            disabled={props.busy}
            className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
            onClick={props.onSave}
          >
            {props.busy ? "Saving…" : "Save plant"}
          </button>
        </div>
        {props.error ? <p className="text-sm text-red-600">{props.error}</p> : null}
      </div>
    </section>
  );
}

export function emptyPartyDraft(): PartyDraft {
  return {
    id: null,
    name: "",
    program: "tube_steel",
    color: "#2563eb",
    pricingUrl: "",
    address: "",
    preferredMiles: "",
    fullMiles: "",
    polygonText: "",
  };
}

export function draftFromParty(party: CoveragePartyInput, areas: CoverageAreaInput[]): PartyDraft {
  const preferred = areas.find((area) => area.partyId === party.id && area.kind === "circle" && area.tier === "preferred");
  const full = areas.find((area) => area.partyId === party.id && area.kind === "circle" && area.tier === "full");
  const polygon = areas.find((area) => area.partyId === party.id && area.kind === "polygon");
  return {
    id: party.id,
    name: party.name,
    program: party.program,
    color: party.color || "#2563eb",
    pricingUrl: party.pricingUrl,
    address: "",
    preferredMiles: preferred?.radiusKm ? String(kmToMiles(preferred.radiusKm)) : "",
    fullMiles: full?.radiusKm ? String(kmToMiles(full.radiusKm)) : "",
    polygonText: polygon?.geojson ? JSON.stringify(polygon.geojson) : "",
  };
}
